import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!token) return;
    api.get(`/auth/invitation/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Invitation not found"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setErr("Passwords do not match");
    if (pw.length < 8) return setErr("Password must be at least 8 characters");
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/accept-invitation", { token, password: pw });
      toast.success("Account activated");
      await refresh();
      nav("/", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) return <div className="p-8">Missing token.</div>;
  if (err && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="brut-card p-8 max-w-md brut-shadow-lg">
          <div className="font-chivo font-black text-2xl uppercase tracking-tighter">Invitation invalid</div>
          <div className="mt-2 text-sm">{err}</div>
          <Link to="/login" className="mt-6 inline-block border border-black px-3 py-1.5 text-xs uppercase tracking-widest">Back to sign in</Link>
        </div>
      </div>
    );
  }
  if (!info) return <div className="p-8 label-mono">Loading invitation…</div>;

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="brut-card p-8 max-w-md w-full brut-shadow-lg">
        <div className="label-mono">Accept invitation</div>
        <div className="font-chivo font-black text-3xl uppercase tracking-tighter leading-none mt-1">
          Welcome, {info.name?.split(" ")[0] || "there"}
        </div>
        <div className="mt-3 text-sm">
          You&apos;ve been invited to LeadFlow as <span className="font-mono">{info.role}</span>
          {info.organization ? <> in <b>{info.organization.name}</b></> : ""}.
        </div>
        {info.expired ? (
          <div className="mt-4 border border-red-600 bg-red-50 p-3 text-xs text-red-700">
            This invitation has expired. Ask an administrator to resend it.
          </div>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={submit}>
            <div>
              <label className="label-mono">Email</label>
              <input value={info.email} disabled className="w-full border border-black px-3 py-2 mt-1 bg-zinc-100" />
            </div>
            <div>
              <label className="label-mono">Set password</label>
              <input
                data-testid="invite-password"
                type="password" required
                className="w-full border border-black px-3 py-2 mt-1 bg-white focus:outline-none focus:ring-2 focus:ring-black"
                value={pw} onChange={(e) => setPw(e.target.value)}
              />
            </div>
            <div>
              <label className="label-mono">Confirm password</label>
              <input
                data-testid="invite-password-confirm"
                type="password" required
                className="w-full border border-black px-3 py-2 mt-1 bg-white focus:outline-none focus:ring-2 focus:ring-black"
                value={pw2} onChange={(e) => setPw2(e.target.value)}
              />
            </div>
            {err && <div className="border border-red-600 bg-red-50 p-2 text-xs text-red-700">{err}</div>}
            <button
              data-testid="invite-submit"
              disabled={loading}
              className="w-full bg-black text-white py-3 uppercase text-xs tracking-widest font-bold border border-black hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100 disabled:opacity-60"
            >
              {loading ? "Activating…" : "Activate account"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
